import React from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Badge,
} from '../ui'
import { useDeleteOrganization } from '../../hooks/organizations'
import type { Organization } from '../../types/organization.types'
import { LoadingSpinner } from '../ui/loading-states'

interface DeleteOrganizationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization: Organization | null
}

export function DeleteOrganizationDialog({
  open,
  onOpenChange,
  organization
}: DeleteOrganizationDialogProps) {
  const deleteOrganization = useDeleteOrganization()
  const isLoading = deleteOrganization.isPending

  const handleDelete = async () => {
    if (!organization) return

    try {
      await deleteOrganization.mutateAsync(organization.id)
      onOpenChange(false)
    } catch (error) {
      console.error('Delete error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  if (!organization) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle>
                <span className="text-red-900">Удалить организацию?</span>
              </DialogTitle>
              <DialogDescription>
                Это действие нельзя будет отменить.
              </DialogDescription>
            </div>
          </div>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Название:</span>
                <p className="text-sm text-gray-900">{organization.name}</p>
              </div>

              {organization.legal_name && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Полное наименование:</span>
                  <p className="text-sm text-gray-900">{organization.legal_name}</p>
                </div>
              )}

              <div>
                <span className="text-sm font-medium text-gray-700">БИН:</span>
                <p className="text-sm text-gray-900 font-mono">{organization.bin}</p>
              </div>

              {organization.code_1c && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Код 1С:</span>
                  <p className="text-sm text-gray-900">{organization.code_1c}</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Статус:</span>
                {organization.is_headquarters ? (
                  <Badge variant="default">Головная</Badge>
                ) : (
                  <Badge variant="outline">Дочерняя</Badge>
                )}
                {organization.code_1c ? (
                  <Badge variant="secondary">1С</Badge>
                ) : (
                  <Badge variant="outline">TCO</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Warning about consequences */}
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Внимание!</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Организация будет деактивирована</li>
                  <li>Сотрудники, отделы и должности потеряют связь с организацией</li>
                  <li>Данные можно будет восстановить через 1С синхронизацию</li>
                  {organization.is_headquarters && (
                    <li className="text-red-600 font-medium">
                      Это головная организация холдинга!
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Show API errors */}
          {deleteOrganization.error && (
            <div className="mt-4 flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(deleteOrganization.error as any)?.message || 'Произошла ошибка при удалении'}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Отменить
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Удаление...
              </>
            ) : (
              'Удалить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
